/**
 * Card de integração Google Ads (status + conectar/reconectar).
 */
import React from 'react';
import { GoogleAdsLogoIcon } from '../../../components/icons/CustomIcons';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import type { GoogleAdsStatus } from '../../../services/modules/googleAdsRepository';

interface GoogleAdsCardProps {
    status: GoogleAdsStatus;
    connecting: boolean;
    onConnect: () => void;
    onDisconnect: () => void;
    onReconnect: () => void;
    /** available libera a conexão. Os outros estados bloqueiam o card. */
    availability?: 'available' | 'coming_soon' | 'unavailable';
}

const GoogleAdsCard: React.FC<GoogleAdsCardProps> = ({
    status,
    connecting,
    onConnect,
    onDisconnect,
    onReconnect,
    availability = 'available',
}) => {
    if (availability !== 'available') {
        return (
            <Card>
                <Card.Content>
                    <div className="flex items-center gap-3 mb-4">
                        <GoogleAdsLogoIcon className="w-10 h-10" />
                        <h3 className="text-section-title text-default">Google Ads</h3>
                    </div>
                    <p className="text-body text-secondary mb-4">
                        {availability === 'coming_soon'
                            ? 'Em breve no seu plano.'
                            : 'Disponível nos planos Pro e Enterprise.'}
                    </p>
                    {availability !== 'coming_soon' && (
                        <Button
                            type="button"
                            variant="secondary"
                            className="w-full"
                            onClick={() => {
                                window.location.assign('/faturamento');
                            }}
                        >
                            Fazer upgrade
                        </Button>
                    )}
                </Card.Content>
            </Card>
        );
    }

    const expired = status.status === 'expired';
    const pending = status.status === 'pending';
    const connected = status.connected && !expired;

    return (
        <Card>
            <Card.Content>
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <GoogleAdsLogoIcon className="w-10 h-10" />
                        <h3 className="text-section-title text-default">Google Ads</h3>
                    </div>
                    <span
                        className={`px-3 py-1 rounded-full text-2xs font-medium ${
                            connected
                                ? 'bg-success-subtle text-success'
                                : expired || pending
                                    ? 'bg-warning-subtle text-warning'
                                    : 'bg-surface-subtle text-secondary'
                        }`}
                    >
                        {connected
                            ? 'Conectado'
                            : expired
                                ? 'Expirado'
                                : pending
                                    ? 'Pendente'
                                    : 'Desconectado'}
                    </span>
                </div>
                <p className="text-body text-secondary mb-4">
                    {connected
                        ? `Conta: ${status.customerName || status.customerId}. Conversões no ganho para as ações selecionadas.`
                        : expired
                            ? 'A autorização Google expirou. Reconecte para escolher a conta e as conversões.'
                            : pending
                                ? 'Autorização Google ok — escolha a conta de anúncios e as ações de conversão.'
                                : 'Conecte sua conta para atribuir cliques (gclid) e devolver vendas no Ganho.'}
                </p>
                {connected ? (
                    <div className="flex flex-col gap-2">
                        <Button onClick={onDisconnect} className="w-full" variant="secondary">
                            Desconectar
                        </Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        <Button
                            onClick={expired ? onReconnect : onConnect}
                            className="w-full"
                            variant="secondary"
                            disabled={connecting}
                        >
                            {connecting
                                ? 'Conectando…'
                                : expired
                                    ? 'Reconectar'
                                    : pending
                                        ? 'Continuar configuração'
                                        : 'Integrar com Google'}
                        </Button>
                        {pending && (
                            <Button
                                onClick={onReconnect}
                                className="w-full"
                                variant="ghost"
                                disabled={connecting}
                            >
                                Recomeçar conexão
                            </Button>
                        )}
                    </div>
                )}
            </Card.Content>
        </Card>
    );
};

export default GoogleAdsCard;
